import { ChangeEvent, FormEvent, FunctionComponent, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { ApiResources } from 'types/apiResources';
import { Option } from 'types/options';
import ListCollection from 'types/redux/ListCollection';
import { Routes } from 'types/routes';

import Company from 'models/Company';

import { parseToQueryString } from 'utils/querystring';

import { list as listCourses } from 'requests/companies';

import CompaniesCards from 'components/content/sections/CompaniesCards';
import Form from 'components/layout/forms/Form';
import Input from 'components/layout/forms/Input';
import LayoutContainer, { Sizes } from 'components/layout/LayoutContainer';
import Pagination from 'components/layout/Pagination';
import Spinner from 'components/layout/Spinner/component';
import useIsFirstRender from 'hooks/useIsFirstRender';

import StyledComponent from './styles';
import { Props } from './types';

interface FormState {
    search: string;
}

interface HandleValue {
    [key: string]: string | number | Option<any>;
}

const SectionCompanies: FunctionComponent<Props> = ({ translations, companies }) => {
    const [formValues, setFormValues]: [FormState, Function] = useState(null);
    const [companiesList, setCompaniesList] = useState(companies);

    const router = useRouter();
    const isFirstRender = useIsFirstRender();

    const isNotEmpty = companiesList?.elements?.length > 0;
    const isEmpty = companiesList?.elements?.length === 0;

    const onSubmit = (event: FormEvent<HTMLFormElement> = null, page: number = 1) => {
        event?.preventDefault();

        setCompaniesList((prevState: ListCollection<Company>) => ({
            ...prevState,
            isLoaded: false,
            isLoading: true,
        }));

        listCourses({
            page,
            perPage: 6,
            search: formValues?.search,
        })
            .then((response) => {
                setCompaniesList((prevState: ListCollection<Company>) => ({
                    ...prevState,
                    ...response?.[ApiResources.Companies],
                    elements: response?.[ApiResources.Companies]?.elements,
                }));

                if (response?.[ApiResources.Companies]?.elements?.length > 0) {
                    router.push(Routes.PublicCourses, { hash: 'results' }, { shallow: true });
                }
            });
    };

    const handleChange = (value: HandleValue) => setFormValues((prevState: FormState) => ({ ...prevState, ...value }));

    useEffect(() => {
        let timeOut: NodeJS.Timeout;


        if (!isFirstRender) timeOut = setTimeout(() => {
            onSubmit(null);
        }, 500);

        return () => clearTimeout(timeOut);
    }, [formValues]);

    return (
        <StyledComponent className="section-companies">
            <LayoutContainer size={Sizes.Medium}>
                <Form
                    className="companies-serach-form"
                    onSubmit={onSubmit}
                >
                    <Input
                        name="search"
                        type="text"
                        className="input-search"
                        placeholder={translations?.inputSearch}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange({ search: e.target.value })}
                        value={formValues?.search}
                    />
                </Form>
                <div
                    className="companies-results"
                    id="results"
                >
                    {companiesList?.isLoading && (
                        <Spinner />
                    )}
                    {(companiesList?.isLoaded && isNotEmpty) && (
                        <>
                            <CompaniesCards
                                companies={companiesList}
                                translations={translations}
                            />
                            <Pagination
                                currentPage={companiesList?.meta?.currentPage}
                                lastPage={companiesList?.meta?.lastPage}
                                hrefBuilder={(currentPage) => parseToQueryString(Routes.PublicCompanies, { page: currentPage })}
                                onPageChange={(nextPage: number) => {
                                    if (nextPage === companiesList?.meta?.currentPage) return;

                                    onSubmit(undefined, nextPage);
                                }}
                            />
                        </>
                    )}
                    {(companiesList?.isLoaded && isEmpty) && (
                        <div className="companies-results-empty">
                            <p className="empty-title">
                                {translations?.emptyResultsInfo}
                            </p>
                            <p className="empty-description">
                                {translations?.emptyResultsTextOne}
                                <span
                                    className="empty-description-click"
                                    onClick={() => {
                                        setFormValues({
                                            search: null,
                                            category: null,
                                        });

                                        if (router?.query?.search) {
                                            router.replace(Routes.PublicCourses, undefined, { shallow: true });
                                        }

                                        onSubmit();
                                    }}
                                >
                                    {translations?.emptyResultsButton}
                                </span>
                                {translations?.emptyResultsTextTwo}
                            </p>
                        </div>
                    )}
                </div>
            </LayoutContainer>
        </StyledComponent>
    );
};

export default SectionCompanies;
